import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  NgZone,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './compoenent/common/header/header.component';
import { SidemenuComponent } from './compoenent/common/sidemenu/sidemenu.component';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { CommonModule, isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MobileHeaderComponent } from './compoenent/mobile/mobile-header/mobile-header.component';
import { MobileBottomComponent } from './compoenent/mobile/mobile-bottom/mobile-bottom.component';
import { MediaCacheService } from './services/media-cache.service';
import { CustomDateModalService } from './services/custom-date-modal.service';
import { SetupStatusService } from './services/setup-status.service';
import { ThemeService } from './services/theme.service';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { LucideIconsModule } from './shared/lucide-icons/lucide-icons.module';
import { OrAppShellComponent, OrDatePickerComponent } from './ui';
import { shouldShowMobileBottomNav } from './utils/mobile-bottom-nav.util';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    SidemenuComponent,
    NzGridModule,
    NgClass,
    CommonModule,
    NgTemplateOutlet,
    MobileHeaderComponent,
    MobileBottomComponent,
    NzModalModule,
    NzButtonModule,
    FormsModule,
    LucideIconsModule,
    OrAppShellComponent,
    OrDatePickerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  showLayout = false;
  isExpanded = true;
  currentRoute: string = '';
  /** Resolved synchronously on the browser so mobile never flashes the desktop shell. */
  currentScreen: 'Mobile' | 'Tablet' | 'Desktop' = 'Desktop';
  readonly customDate = inject(CustomDateModalService);

  get isMobileScreen(): boolean {
    return this.currentScreen === 'Mobile';
  }

  get showMobileChrome(): boolean {
    if (!this.isMobileScreen || !this.currentRoute) return false;
    const r = this.currentRoute;
    if (r === '/login' || r === '/' || r === '/signup-success') return false;
    if (r.startsWith('/otp-verification')) return false;
    if (r.startsWith('/sign-up')) return false;
    if (r.startsWith('/mobile-settings')) return false;
    return true;
  }

  /**
   * Bottom nav on phones — only on primary dock destinations (Home, Members,
   * Loyalty, Rewards). Hide for More-menu pages, settings, CRUD/builders, and
   * while a bottom sheet / overlay is open (see or-sheet + html.or-mobile-overlay-open).
   */
  get showMobileBottomNav(): boolean {
    if (!this.showLayout) return false;
    return shouldShowMobileBottomNav(this.currentRoute || '');
  }

  private route = inject(Router);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private mediaCache = inject(MediaCacheService);
  private setupStatus = inject(SetupStatusService);
  private theme = inject(ThemeService);
  /** Avoid stacking duplicate setup redirects on rapid NavigationEnd bursts. */
  private setupCheckSub?: { unsubscribe: () => void };

  /**
   * Routes where we should NOT trigger the setup-status redirect.
   * These flows either don't need a token or are already part of the
   * onboarding journey, so re-checking would cause loops/noise.
   */
  private readonly setupCheckSkipRoutes = [
    '/login',
    '/otp-verification',
    '/forgot-password',
    '/sign-up',
    '/signup-success',
  ];

  // ngOnInit() {
  //   this.route.events.subscribe((data: any) => {
  //     if (data instanceof NavigationEnd) {
  //       this.currentRoute = data.urlAfterRedirects.split('?')[0].split(';')[0];
  //       this.showLayout =
  //         this.currentRoute !== '/login' &&
  //         this.currentRoute !== '/' &&
  //         !this.currentRoute.startsWith('/mobile-settings');
  //       !this.currentRoute.startsWith('/feedback/builder') &&
  //         !this.currentRoute.startsWith('/feedback/responses');
  //     }
  //   });
  // }

  ngOnInit() {
    this.theme.init();
    this.mediaCache.register();
    this.route.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data instanceof NavigationEnd) {
        const url = data.urlAfterRedirects.split('?')[0].split(';')[0];
        this.currentRoute = url;

        const noLayoutRoutes = ['/login', '/', '/signup-success', '/forgot-password'];

        const startsWithRoutes = [
          '/mobile-settings',
          '/otp-verification',
          '/forgot-password',
          '/sign-up',
          '/feedback/builder',
          '/signup-success',
          '/outlet',
        ];

        // Should show header + sidebar?
        this.showLayout =
          !noLayoutRoutes.includes(url) &&
          !startsWithRoutes.some((r) => url.startsWith(r));

        this.runSetupStatusCheck(url);
        this.cdr.markForCheck();
      }
    });

    this.runSetupStatusCheck();
  }

  /**
   * Triggers the setup-status check once on project init so that any
   * user landing on the app with an incomplete onboarding is sent to the
   * correct sign-up step (details / outlets / integration / pos).
   */
  private runSetupStatusCheck(url?: string): void {
    if (!this.isBrowser) return;

    const token =
      localStorage.getItem('TtZUw0Q87E647ac8j0gQHiCmBhczph') || '';
    if (!token) return;

    const resolvedUrl = (url ?? this.route.url ?? '').split('?')[0];
    const shouldSkip = this.setupCheckSkipRoutes.some(
      (r) => resolvedUrl === r || resolvedUrl.startsWith(r + '/') || resolvedUrl.startsWith(r),
    );
    if (shouldSkip) return;

    this.setupCheckSub?.unsubscribe();
    this.setupCheckSub = this.setupStatus
      .checkAndRedirect(resolvedUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  constructor(private breakpointObserver: BreakpointObserver) {
    // Set before first paint — defaulting to Desktop then flipping to Mobile
    // remounts router-outlet and can drop the post-login /dashboard navigation.
    if (this.isBrowser) {
      this.currentScreen = this.screenFromWidth(window.innerWidth);
    }

    this.breakpointObserver
      .observe([
        '(max-width: 767px)', // Mobile — matches frontend rules (<768)
        '(min-width: 768px) and (max-width: 1023px)', // Tablet
        '(min-width: 1024px)', // Desktop
      ])
      .pipe(takeUntilDestroyed())
      .subscribe((result: any) => {
        if (result.breakpoints['(max-width: 767px)']) {
          this.currentScreen = 'Mobile';
        } else if (
          result.breakpoints['(min-width: 768px) and (max-width: 1023px)']
        ) {
          this.currentScreen = 'Tablet';
        } else if (result.breakpoints['(min-width: 1024px)']) {
          this.currentScreen = 'Desktop';
        }
        this.cdr.markForCheck();
      });
  }

  private screenFromWidth(width: number): 'Mobile' | 'Tablet' | 'Desktop' {
    if (width < 768) return 'Mobile';
    if (width < 1024) return 'Tablet';
    return 'Desktop';
  }
  private scrollHandler?: (e: Event) => void;
  private numberInputWheelHandler?: (e: WheelEvent) => void;
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Keep scroll/wheel handlers outside Angular so they don't thrash CD.
    this.zone.runOutsideAngular(() => {
      this.scrollHandler = (e: Event) => {
        const target = e.target;

        // Target may be Document or HTMLElement
        const el =
          target instanceof Document
            ? target.documentElement
            : target instanceof HTMLElement
              ? target
              : null;

        if (!el) return;

        el.classList.add('is-scrolling');

        const anyEl: any = el;
        if (anyEl.__scrollTimer) clearTimeout(anyEl.__scrollTimer);

        anyEl.__scrollTimer = setTimeout(() => {
          el.classList.remove('is-scrolling');
        }, 900);
      };

      document.addEventListener('scroll', this.scrollHandler, {
        capture: true,
        passive: true,
      });

      // Prevent mouse wheel from increasing/decreasing values on focused
      // number inputs across the entire app.
      this.numberInputWheelHandler = (e: WheelEvent) => {
        const active = document.activeElement;
        if (!(active instanceof HTMLInputElement)) return;

        const isNativeNumberInput = active.type === 'number';
        const isAntNumberInput = active.classList.contains(
          'ant-input-number-input',
        );

        if (!isNativeNumberInput && !isAntNumberInput) return;

        e.preventDefault();
        active.blur();
      };
      document.addEventListener('wheel', this.numberInputWheelHandler, {
        passive: false,
        capture: true,
      });
    });
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.scrollHandler) {
      document.removeEventListener('scroll', this.scrollHandler, true);
    }

    if (this.numberInputWheelHandler) {
      document.removeEventListener(
        'wheel',
        this.numberInputWheelHandler,
        true,
      );
    }
  }

  // ngAfterViewInit(): void {
  //   // Auto-hide scrollbars globally: show on scroll for a moment
  //   this.scrollHandler = (e: Event) => {
  //     const el = e.target as HTMLElement;
  //     if (!el || !(el instanceof HTMLElement)) return;
  //     el.classList.add('is-scrolling');
  //     const anyEl: any = el as any;
  //     if (anyEl.__scrollTimer) clearTimeout(anyEl.__scrollTimer);
  //     anyEl.__scrollTimer = setTimeout(() => {
  //       el.classList.remove('is-scrolling');
  //     }, 900);
  //   };
  //   document.addEventListener('scroll', this.scrollHandler, true);
  // }

  // ngOnDestroy(): void {
  //   if (this.scrollHandler) {
  //     document.removeEventListener('scroll', this.scrollHandler, true);
  //   }
  // }

  // ngAfterViewInit(): void {
  //   if (!this.isBrowser) return;
  //   this.zone.runOutsideAngular(() => {
  //     this.scrollHandler = (e: Event) => {
  //       const el = e.target as HTMLElement;
  //       if (!el || !(el instanceof HTMLElement)) return;
  //       el.classList.add('is-scrolling');
  //       const existingTimer = this.scrollTimers.get(el);
  //       if (existingTimer) {
  //         clearTimeout(existingTimer);
  //       }
  //       const newTimer = window.setTimeout(() => {
  //         this.zone.run(() => {
  //           el.classList.remove('is-scrolling');
  //           this.scrollTimers.delete(el);
  //         });
  //       }, 900);
  //       this.scrollTimers.set(el, newTimer);
  //     };
  //     document.addEventListener('scroll', this.scrollHandler, {
  //       capture: true,
  //       passive: true,
  //     });
  //   });
  // }

  // ngOnDestroy(): void {
  //   if (!this.isBrowser) return;
  //   if (this.scrollHandler) {
  //     document.removeEventListener('scroll', this.scrollHandler, true);
  //   }
  //   this.scrollTimers = new WeakMap();
  // }

  toggleMenu() {
    this.isExpanded = !this.isExpanded;
  }

  get isCustomDateMobile(): boolean {
    return this.currentScreen === 'Mobile';
  }

  get customDateModalWidth(): string {
    return this.isCustomDateMobile ? 'calc(100vw - 24px)' : '720px';
  }

  get customDateModalClass(): string {
    return this.isCustomDateMobile
      ? 'custom-date-modal-overlay custom-date-modal-overlay--mobile'
      : 'custom-date-modal-overlay';
  }

  get customDateBodyStyle(): Record<string, string> {
    return this.isCustomDateMobile ? { padding: '0' } : { padding: '24px 28px 28px' };
  }
}
